// Test & Refine redesign - shell.
//
// Presentational root for the flag-gated redesign. It owns NO data state:
// every service call, orchestration handler, and data prop is passed in
// from AISandboxPage (the orchestrator keeps its contract). The shell only
// holds view-only UI state (which tab, and delegates the rest to the views)
// and routes between Configure / Results / History.
import type { ReactNode } from "react";
import { FlaskConical, CheckCircle2, Clock, Lock, Play } from "lucide-react";
import "./test-refine-redesign.css";
import { TrOrb } from "./shared";

import type { AIModel } from "@/app/constants/models";
import type {
  SimulationQueueItem,
  MonitoringBeforeData,
  AISandboxData,
  SandboxSuggestion,
} from "@/app/services/simulation/ai-sandbox-service";

import { ConfigureView } from "./configure-view";
import { RunningView } from "./running-view";
import { ResultsView } from "./results-view";
import { HistoryView } from "./history-view";

export type MonitoringSource = "live" | "cached" | "none";

/** An optional AI-authored proposal. Absent until a real backend source
 *  supplies it; when absent, the proposal band stays hidden (never
 *  fabricated). Kept here so the wiring seam is ready for Gagan. */
export interface TestRefineProposal {
  query: string;
  beforePct: number;
  predictedPct: number;
  targetActionIds: number[];
  themeLabel: string;
  currentRank: number;
}

export interface TestRefineShellProps {
  brand: string;
  sandboxData: AISandboxData;
  monitoringData: MonitoringBeforeData | null;
  monitoringSource: MonitoringSource;

  availableModels: AIModel[];
  selectedModels: string[];
  setSelectedModels: (ids: string[]) => void;
  onModelChangeContext?: (id: string, added: boolean, count: number) => void;

  query: string;
  setQuery: (q: string) => void;

  activeTab: string;
  setActiveTab: (t: string) => void;

  isSimulating: boolean;
  hasRunSimulation: boolean;

  competitors: string[];
  competitorInput: string;
  setCompetitorInput: (v: string) => void;
  addCompetitor: () => void;
  removeCompetitor: (name: string) => void;

  selectedQueueItems: number[];
  setSelectedQueueItems: (ids: number[]) => void;

  selectedFile: File | null;
  setSelectedFile: (f: File | null) => void;

  onViewContent: (item: SimulationQueueItem) => void;

  appliedQueueItems: SimulationQueueItem[];
  onRunSimulation: () => void;
  onHistoryClick: (runId: number) => void;

  /** AI-proposed complete tests (question + actions + why) + loading state. */
  suggestions: SandboxSuggestion[];
  suggestionsLoading: boolean;

  /** Opens the AI assistant, optionally with a topic prefilled. */
  onOpenAIChat: (topic?: string) => void;

  /** Optional AI proposal. When undefined the band is hidden. */
  proposal?: TestRefineProposal;
}

const TABS: Array<{ value: string; label: string }> = [
  { value: "configure", label: "Configure" },
  { value: "results", label: "Results" },
  { value: "history", label: "History" },
];

export function TestRefineShell(props: TestRefineShellProps) {
  const {
    brand,
    sandboxData,
    activeTab,
    setActiveTab,
    selectedModels,
  } = props;

  const queue = sandboxData.simulationQueue || [];
  const history = sandboxData.simulationHistory || [];

  const queuedCount = queue.length;
  const modelSelCount = selectedModels.length;
  const runCount = history.length;

  // Horizontal gutter is handled by the app-standard px-4 md:px-8 wrapper
  // below (matches the rest of the app), so sections carry vertical padding
  // only. Full width, no centered max-width column.
  const PADDING = "0px";

  return (
    <div className="tr-root" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <div className="w-full px-4 md:px-8">
        {/* ── Module header ─────────────────────────────────────── */}
        {/* Header carries its own px-4 md:px-8 (like AntennaHeader), so with
            the outer wrapper its content lands at the same ~64px gutter as
            Brand Monitoring's header and the body cards. */}
        <header className="px-4 md:px-8" style={{ paddingTop: 26, paddingBottom: 20, borderBottom: "1px solid var(--border)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "var(--muted-foreground)",
                fontWeight: 500,
              }}
            >
              <FlaskConical size={15} strokeWidth={1.7} />
              Optimize
            </span>
            <span style={{ color: "var(--silver-mist)" }}>/</span>
            <span style={{ color: "var(--foreground)", fontWeight: 600 }}>Test &amp; Refine</span>
          </div>

          <h1
            style={{
              fontSize: "clamp(24px, 3.5vw, 30px)",
              fontWeight: 600,
              letterSpacing: "-.02em",
              lineHeight: 1.25,
              color: "var(--foreground)",
              margin: "0 0 8px",
            }}
          >
            Test changes before you make them.
          </h1>
          <p
            style={{
              fontSize: 15,
              color: "var(--muted-foreground)",
              margin: "0 0 18px",
              textWrap: "pretty",
            }}
          >
            Run a private simulation to see how AI would describe and recommend {brand || "your brand"} if
            you shipped your queued actions. Nothing goes live.
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--muted-foreground)", fontWeight: 500 }}>Sandbox</span>

            <StatItem>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  background: "var(--alpha-teal)",
                  flexShrink: 0,
                }}
              />
              <StatNum>{queuedCount}</StatNum>
              <StatLabel>actions ready to test</StatLabel>
            </StatItem>

            <StatItem>
              <CheckCircle2 size={14} strokeWidth={1.9} style={{ color: "var(--signal-green)" }} />
              <StatNum>{modelSelCount}</StatNum>
              <StatLabel>models selected</StatLabel>
            </StatItem>

            <StatItem>
              <Clock size={14} strokeWidth={1.8} style={{ color: "var(--spectral-violet)" }} />
              <StatNum>{runCount}</StatNum>
              <StatLabel>simulations run</StatLabel>
            </StatItem>

            <span
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Lock size={13} strokeWidth={1.8} style={{ color: "var(--signal-green)" }} />
              Private sandbox, nothing goes live
            </span>
          </div>
        </header>

        {/* ── Tabs (underline, 16px, active teal) ───────────────── */}
        <div
          className="px-4 md:px-8"
          style={{
            marginTop: 14,
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            gap: 28,
          }}
        >
          {TABS.map((t) => {
            const active = activeTab === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setActiveTab(t.value)}
                style={{
                  position: "relative",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "14px 0",
                  fontSize: 16,
                  fontWeight: active ? 600 : 500,
                  color: active ? "var(--foreground)" : "var(--muted-foreground)",
                }}
              >
                {t.label}
                {active && (
                  <span
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: -1,
                      height: 2,
                      background: "var(--alpha-teal)",
                      borderRadius: "2px 2px 0 0",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* ── Bodies ────────────────────────────────────────────── */}
        {/* Body sits in a second px-2 md:px-8, matching Brand Monitoring's
            nested body padding (header stays at the outer px-4 md:px-8). */}
        {activeTab === "configure" && (
          <div className="px-2 md:px-8">
            <ConfigureView {...props} padding={PADDING} />
          </div>
        )}

        {activeTab === "results" && (
          <div className="px-2 md:px-8" style={{ paddingTop: 24, paddingBottom: 60 }}>
            {props.isSimulating ? (
              <RunningView
                query={props.query}
                modelIds={selectedModels}
                actionsApplied={props.selectedQueueItems.length}
              />
            ) : props.hasRunSimulation ? (
              <ResultsView
                sandboxData={sandboxData}
                query={props.query}
                brand={brand}
                appliedQueueItems={props.appliedQueueItems}
                monitoringSource={props.monitoringSource}
                selectedModelCount={modelSelCount}
                onOpenAIChat={props.onOpenAIChat}
              />
            ) : (
              <EmptyResults onConfigure={() => setActiveTab("configure")} />
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="px-2 md:px-8" style={{ paddingTop: 24, paddingBottom: 60 }}>
            <HistoryView history={history} onSelectRun={props.onHistoryClick} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Small header helpers ──────────────────────────────────────────
function StatItem({ children }: { children: ReactNode }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{children}</div>;
}
function StatNum({ children }: { children: ReactNode }) {
  return (
    <strong style={{ fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{children}</strong>
  );
}
function StatLabel({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>{children}</span>;
}

function EmptyResults({ onConfigure }: { onConfigure: () => void }) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: "56px 24px",
        textAlign: "center",
        background: "var(--muted)",
      }}
    >
      <div style={{ display: "inline-flex", marginBottom: 14 }}>
        <TrOrb size={44} radius={12} iconSize={22} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--foreground)", marginBottom: 5 }}>
        No simulation yet
      </div>
      <p
        style={{
          fontSize: 13.5,
          color: "var(--muted-foreground)",
          margin: "0 auto 16px",
          maxWidth: 380,
        }}
      >
        Configure a query and actions, then run a simulation to see the before to after impact here.
      </p>
      <button
        onClick={onConfigure}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 13,
          fontWeight: 600,
          color: "var(--on-teal-text)",
          background: "var(--alpha-teal)",
          border: "none",
          borderRadius: 9,
          padding: "9px 16px",
          cursor: "pointer",
        }}
      >
        <Play size={14} strokeWidth={1.8} />
        Go to Configure
      </button>
    </div>
  );
}
